import { type ButtonHTMLAttributes, Children, Fragment, type ReactNode, useId, useState } from "react";

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

export function Card({ children, flush, className }: { children: ReactNode; flush?: boolean; className?: string }) {
  return <div className={cx("card", flush && "card--flush", className)}>{children}</div>;
}

export function Muted({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx("muted", className)}>{children}</p>;
}

/** Collapsible section; keeps advanced inputs out of the way until needed. */
export function Section({
  title,
  summary,
  initiallyOpen = false,
  children,
}: {
  title: string;
  summary?: string;
  initiallyOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const bodyId = useId();
  return (
    <div className="section">
      <button
        type="button"
        className="section__header"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="grow">
          <span className="section__title">{title}</span>
          {summary && !open ? <span className="section__summary">{summary}</span> : null}
        </span>
        <span className="section__chevron" aria-hidden>
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? (
        <div className="section__body" id={bodyId}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** One card holding several sections, separated by hairlines — a single quiet list instead of a stack of boxes. */
export function SectionGroup({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  return (
    <Card flush>
      {items.map((child, i) => (
        <Fragment key={i}>
          {i > 0 ? <div className="divider" /> : null}
          {child}
        </Fragment>
      ))}
    </Card>
  );
}

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

export function Button({
  title,
  variant = "primary",
  loading,
  flex,
  block,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  title: string;
  variant?: ButtonVariant;
  loading?: boolean;
  /** Share a row equally with sibling buttons. */
  flex?: boolean;
  block?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      aria-busy={loading || undefined}
      className={cx("btn", `btn--${variant}`, flex && "btn--flex", block && "btn--block", props.className)}
    >
      {loading ? <span className="spinner" aria-hidden /> : null}
      {title}
    </button>
  );
}

export type BadgeTone = "accent" | "warning" | "negative" | "info" | "neutral";

export function Badge({ label, tone = "neutral" }: { label: string; tone?: BadgeTone }) {
  return <span className={`badge badge--${tone}`}>{label}</span>;
}

/** Segmented control for small mutually exclusive choices (a native radio group). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  /** Accessible name of the group. */
  label: string;
}) {
  const name = useId();
  return (
    <fieldset className="segmented" aria-label={label}>
      {options.map((o) => (
        <label key={o.value}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={o.value === value}
            onChange={() => onChange(o.value)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="switch-row">
      <span>{label}</span>
      <input
        type="checkbox"
        role="switch"
        className="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

/** Opens in a new tab; used for official fee sources. */
export function ExternalLink({ href, children, label }: { href: string; children: ReactNode; label?: string }) {
  return (
    <a className="link" href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
      {children}
    </a>
  );
}
